import {
    FC,
    RefObject,
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { CasinoGameTypeEnum } from "@finbackoffice/fe-core";
import { CasinoScopesMap } from "@finbackoffice/site-core";
import { OnChangeHandler } from "components/base/input-field/Input";
import Compact from "./layouts/compact/Compact";

type Props = {
    gameTypes: CasinoGameTypeEnum[];
    showSearchInputDefault?: boolean;
};

export type ICasinoSearchBlockContext = Props & {
    showSearchInput: boolean;
    setShowSearchInput: (val: boolean) => void;
    minSearchCharCount: number;
    searchContainerRef: RefObject<HTMLDivElement | null>;
    searchValue: string;
    handleInnerIconClick: () => void;
    handleChange: OnChangeHandler<false>;
    showSearchContainer: boolean;
};

export const CasinoSearchBlockContext = createContext<ICasinoSearchBlockContext>(null as any);

const CasinoSearchBlock: FC<Props> = (props) => {
    const { gameTypes, showSearchInputDefault = true } = props;
    const isSlots = gameTypes === CasinoScopesMap.slots;
    const [searchValue, setSearchValue] = useState("");
    const [showSearchInput, setShowSearchInput] = useState(showSearchInputDefault);
    const [showSearchContainer, setShowSearchContainer] = useState(false);
    const searchContainerRef: RefObject<HTMLDivElement | null> = useRef(null);
    const minSearchCharCount = isSlots ? 2 : 3;

    const handleChange = useCallback(
        (query: string) => {
            setSearchValue(query);

            if (query.length >= minSearchCharCount) {
                if (!showSearchContainer) {
                    setShowSearchContainer(true);
                }
            } else {
                setShowSearchContainer(false);
            }
        },
        [minSearchCharCount, showSearchContainer],
    );

    useEffect(() => {
        if (searchValue) {
            setShowSearchContainer(false);
            setSearchValue("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameTypes]);

    useEffect(() => {
        const outsideClickHandler = (event: any) => {
            if (searchContainerRef && !searchContainerRef.current?.contains(event.target)) {
                setShowSearchContainer(false);
            }
        };

        if (showSearchContainer) {
            document.addEventListener("mousedown", outsideClickHandler);
        } else {
            document.removeEventListener("mousedown", outsideClickHandler);
        }

        return () => {
            document.removeEventListener("mousedown", outsideClickHandler);
        };
    }, [showSearchContainer]);

    useEffect(() => {
        if (!searchValue && showSearchContainer) {
            setShowSearchContainer(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    const handleInnerIconClick = useCallback(() => {
        setSearchValue("");
    }, []);

    const value = useMemo(
        () => ({
            gameTypes,
            showSearchInput,
            setShowSearchInput,
            handleInnerIconClick,
            handleChange,
            searchContainerRef,
            searchValue,
            minSearchCharCount,
            showSearchContainer,
        }),
        [
            gameTypes,
            showSearchInput,
            setShowSearchInput,
            handleInnerIconClick,
            handleChange,
            searchValue,
            minSearchCharCount,
            showSearchContainer,
        ],
    );

    return (
        <CasinoSearchBlockContext.Provider value={value}>
            <Compact />
        </CasinoSearchBlockContext.Provider>
    );
};

export default CasinoSearchBlock;
